/**
 * InlineComposer (INBOX-PODIUM-FEEL) — the persistent chat box docked at the
 * bottom of an inbox conversation. Type straight in, a row of quick actions
 * (Insert template, Insert field, Review link, Request payment, Note), and Send.
 * Texts go out the office Podium line via podium-send (consent + comms-gate +
 * do-not-text enforced server-side). Personalization (first name, address, …)
 * resolves per-customer and is business/rental-aware via the merge-field engine.
 */
import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Send, StickyNote, Loader2, FileText, Star, DollarSign } from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { TemplatePicker } from "@/features/customer-360/CustomerMessages";
import { useCustomerMergeContext, applyMergeFields, MergeFieldMenu } from "@/lib/mergeFields";
import { useAssignableUsers } from "./AssigneeMenu";

const p10 = (s: string) => s.replace(/\D/g, "").slice(-10);

/** Editable org settings + the one-tap Payment template, loaded once. */
function useComposerExtras() {
  return useQuery({
    queryKey: ["composer_extras"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const [{ data: rl }, { data: pay }] = await Promise.all([
        supabase.from("org_setting").select("value").eq("key", "review_link").maybeSingle(),
        supabase.from("reply_template").select("body").ilike("title", "Payment").eq("archived", false).limit(1).maybeSingle(),
      ]);
      return {
        reviewLink: (rl?.value as string) || "",
        paymentBody: (pay?.body as string) || "",
      };
    },
  });
}

export function InlineComposer({
  custno,
  endpoint,
  name,
  providerName,
}: {
  custno: string;
  endpoint: string | null;
  name: string;
  /** Podium's contact name for the thread — lets the greeting prefer the
   *  actual person texting (a tenant, a PM rep) over the account holder. */
  providerName?: string | null;
}) {
  const qc = useQueryClient();
  const { data: extras } = useComposerExtras();
  const { data: ctx } = useCustomerMergeContext(custno, providerName ?? name);
  const { data: team } = useAssignableUsers();
  const [body, setBody] = useState("");
  const [note, setNote] = useState(false);
  const [sending, setSending] = useState(false);
  const [gated, setGated] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  const to = endpoint ? p10(endpoint) : "";
  const greet = ctx?.firstName ?? (name.split(/[\s,]/)[0] || "there");

  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["conversation_messages"] }),
      qc.invalidateQueries({ queryKey: ["messages"] }),
      qc.invalidateQueries({ queryKey: ["inbox_conversations"] }),
    ]);

  // Insert a merge token at the cursor (or end), keeping focus + caret.
  const insertToken = (token: string) => {
    setNote(false);
    const el = taRef.current;
    if (!el) {
      setBody((b) => b + token);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + token + body.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  // Quick actions stage an editable message — dispatcher reviews + sends.
  const insertReview = () => {
    setNote(false);
    const link = extras?.reviewLink;
    setBody(
      link
        ? `Thanks for choosing Sedgwick Heating, ${greet}! We'd love a quick review — it really helps us out: ${link}`
        : "(No review link set yet — add it in Settings → Post-Install Handoff.)",
    );
  };
  const insertPayment = () => {
    setNote(false);
    setBody(extras?.paymentBody ? applyMergeFields(extras.paymentBody, ctx) : "");
  };

  const send = async () => {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    setErr(null);
    setGated(null);
    try {
      if (note) {
        const { data: u } = await supabase.auth.getUser();
        const { error } = await supabase.from("message").insert({
          custno,
          channel: "note",
          direction: "inbound",
          body: text,
          status: "logged",
          provider: "manual",
          endpoint,
          logged_by: u.user?.email ?? null,
          created_at: new Date().toISOString(),
        });
        if (error) throw error;
        // @mentions in the note → notify those teammates (NOTIFY).
        const mentions = text.match(/@([A-Za-z]+)/g) ?? [];
        if (mentions.length && team) {
          const me = u.user?.email ?? null;
          const recips = new Set<string>();
          for (const mm of mentions) {
            const first = mm.slice(1).toLowerCase();
            const hit = team.find((t) => t.first.toLowerCase() === first);
            if (hit && hit.email !== me) recips.add(hit.email);
          }
          if (recips.size) {
            await supabase.from("notification").insert(
              [...recips].map((r) => ({
                recipient: r,
                type: "mention",
                conversation_key: custno,
                custno,
                title: `${me?.split("@")[0] ?? "Someone"} mentioned you in a note`,
                actor: me,
              })),
            );
          }
        }
        setBody("");
        setNote(false);
        await refresh();
        return;
      }
      if (to.length !== 10) {
        setErr("No phone number on this conversation to text.");
        return;
      }
      const { data, error } = await supabase.functions.invoke("podium-send", {
        body: { to, body: text, custno },
      });
      const detail = (data as { error?: string; blocked?: string } | null) ?? null;
      if (detail?.blocked === "testing" || /testing|paused/i.test(detail?.error ?? "")) {
        setGated("Comms are in testing — at go-live this sends for real. Nothing went to the customer.");
        return;
      }
      if (detail?.blocked === "suppressed") {
        setGated("This number is unsubscribed from texts (do-not-text list). Resubscribe from the ⋮ menu to text again.");
        return;
      }
      if (error || detail?.error) {
        setErr(error?.message ?? detail?.error ?? "send failed");
        return;
      }
      setBody("");
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  };

  const actionBtn = "inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700";

  return (
    <div className="border-t border-hairline bg-white px-4 py-3">
      {gated && <p className="mb-2 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">{gated}</p>}
      {err && <p className="mb-2 rounded-md bg-red-50 px-2.5 py-1.5 text-xs text-red-700">Send failed: {err}</p>}
      <div className={cn("rounded-xl border transition-colors", note ? "border-amber-300 bg-amber-50/40" : "border-hairline bg-white focus-within:border-brand-400")}>
        <textarea
          ref={taRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder={note ? "Internal note — the customer never sees this…" : `Text ${greet} or use a quick action…`}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              send();
            }
          }}
          className="block max-h-64 min-h-[4.5rem] w-full resize-y rounded-t-xl bg-transparent px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none"
        />
        {/* pr clears the two fixed bottom-right bubbles (Ask Pegasus, team chat) so Send is never under them (INBOX-HD). */}
        <div className="flex items-center justify-between gap-2 py-1.5 pl-2 pr-[7.5rem]">
          <div className="flex flex-wrap items-center gap-0.5">
            <TemplatePicker
              custno={custno}
              dropUp
              onPick={(b) => {
                setBody(b);
                setNote(false);
              }}
            />
            <MergeFieldMenu ctx={ctx} dropUp onInsert={insertToken} label="Field" />
            <button type="button" onClick={insertReview} className={actionBtn} title="Stage a review request with your Podium review link">
              <Star className="h-3 w-3" /> Review
            </button>
            <button type="button" onClick={insertPayment} className={actionBtn} title="Stage payment instructions (the Payment template)">
              <DollarSign className="h-3 w-3" /> Payment
            </button>
            <button
              type="button"
              onClick={() => setNote((n) => !n)}
              title="Internal note — never sent to the customer"
              className={cn(
                "inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium transition-colors",
                note ? "bg-amber-100 text-amber-800" : "text-slate-500 hover:bg-slate-100 hover:text-slate-700",
              )}
            >
              <StickyNote className="h-3 w-3" /> Note
            </button>
          </div>
          <button
            type="button"
            onClick={send}
            disabled={sending || !body.trim()}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full bg-brand-700 px-4 text-[12.5px] font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:opacity-50"
          >
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : note ? <FileText className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
            {note ? "Save note" : "Send"}
          </button>
        </div>
      </div>
      <p className="mt-1 text-[11px] text-slate-400">
        {note
          ? "Internal — never sent to the customer."
          : "Sends from the office Podium line · ⌘/Ctrl+Enter to send · review & payment stage an editable message first"}
      </p>
    </div>
  );
}
